import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  split,
  ApolloLink,
  Observable,
  Operation,
  FetchResult
} from '@apollo/client'
import { print } from 'graphql/language/printer'
import { getMainDefinition } from '@apollo/client/utilities'
import { ApolloProvider, useApolloClient } from '@apollo/client'
import {
  create,
  send,
  observe,
  AbsintheSocket,
  Notifier,
  Observer,
  cancel
} from '@absinthe/socket'
import { Socket as PhoenixSocket } from 'phoenix'

import { ShopConfig } from 'shop/types'
import { onError } from '@apollo/client/link/error'
import { removeLoginCredentials } from 'shop/utils'
import { getErrorMessage, isNetworkErrorUnauthorized } from 'shop/utils/common'

const scheme = (proto: string) => {
  if (process.env.NODE_ENV === 'production') {
    return 'https'
  } else {
    if (process.env.REACT_APP_GRAPH_HOST?.includes('slerp.io')) return 'https'
    return window.location.protocol === 'https:' ? `${proto}s` : proto
  }
}

export const consumerClientContext = { context: { clientName: 'consumerApi' } }

export const GRAPHQL_ENDPOINT = `${scheme('http')}://${process.env.REACT_APP_GRAPH_HOST}/v1/graphql`

const WSS_CONSUMER_ENDPOINT = `wss://api.${process.env.REACT_APP_CONSUMER_GRAPH_HOST}/live_orders`

let consumerApiUri: string

/**
 * This creates a new Apollo Client.
 * This is being used by ShopClient or can be called as a standalone function
 *
 * @param domain
 * @param apiKey
 */
const createClient = (domain: string, apiKey: string, partnerId: string) => {
  // Use consumer API when context clientName 'consumerApi' is set, else use Hasura API
  const client = new ApolloClient({
    link: getClientLinks(domain, apiKey, partnerId),
    cache: new InMemoryCache({
      addTypename: false
    })
  })
  return client
}

/** Initialises phoenix socket and sets up websocket observable for live updates */
const createCustomAbsintheLink = (
  url: string,
  params: Record<string, string>,
  errorCallback?: () => any,
  successCallback?: () => any
): { websocketLink: ApolloLink; socket: PhoenixSocket } => {
  const phoenixSocket = new PhoenixSocket(url, {
    params: params,
    reconnectAfterMs: (tries: number) => {
      // Stop reconnecting if we've hit max retries
      if (tries >= 5) {
        return 1000000
      }
      // Exponential backoff: 1s, 2s, 5s, 10s
      const delay = [1000, 2000, 5000, 10000, 10000][tries - 1]
      return delay
    }
  })

  phoenixSocket.connect()
  const absintheSocket: AbsintheSocket = create(phoenixSocket)

  const websocketLink = new ApolloLink((operation: Operation) => {
    return new Observable<FetchResult>((observer) => {
      const notifier: Notifier = send(absintheSocket, {
        operation: print(operation.query),
        variables: operation.variables || {}
      })

      let hasCalledSuccess = false

      const gqlObserver: Observer = {
        onStart: () => {
          if (successCallback && !hasCalledSuccess) {
            hasCalledSuccess = true
            successCallback()
          }
        },
        onAbort: (error) => observer.error(error),
        onError: (error) => {
          if (errorCallback) {
            errorCallback()
          }
          observer.error(error)
        },
        onResult: (result) => {
          observer.next(result)
        }
      }

      let isObserverAttached = true
      observe(absintheSocket, notifier, gqlObserver)

      // Unsubscribes to the websocket observable on teardown
      return () => {
        if (isObserverAttached) {
          try {
            cancel(absintheSocket, notifier)
          } catch (error) {
            console.log('Subscription already unobserved', error)
          } finally {
            isObserverAttached = false
          }
        }
      }
    })
  })

  // Return both link and socket reference
  return { websocketLink, socket: phoenixSocket }
}

/** Returns all links for setting up the Apollo Client */
const getClientLinks = (
  domain: string,
  apiKey: string,
  partnerId: string,
  absintheSocketLink?: ApolloLink
) => {
  const httpLink = createHttpLink({
    uri: GRAPHQL_ENDPOINT,
    headers: {
      authorization: apiKey ? `Bearer ${apiKey}` : ''
    }
  })

  let apiAndWebsocketLink = httpLink
  if (absintheSocketLink) {
    apiAndWebsocketLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query)
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        )
      },
      absintheSocketLink as any,
      httpLink
    )
  }

  const consumerApiLink = createHttpLink({
    uri: consumerApiUri,
    credentials: 'include'
  })

  /** Error link catches unauthorized errors and reset user local values */
  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const isCart = operation.operationName === 'cart'
    const isPartnerAndCustomer =
      operation.operationName === 'PartnerAndCustomer'
    const isPartner = operation.operationName === 'Partner'

    const errorMessage = getErrorMessage(graphQLErrors)

    const isUnauthorized =
      (networkError && isNetworkErrorUnauthorized(networkError)) ||
      (graphQLErrors && ['unauthorized', 'auth_missing'].includes(errorMessage))

    const isNotFound = graphQLErrors && errorMessage === 'not_found'
    const isCartAlreadyOrdered =
      graphQLErrors && errorMessage === 'cart_already_ordered'
    const customerIdDoesNotMatch =
      graphQLErrors && errorMessage === 'customer_id_does_not_match'

    // On 401 not found error and operation is cart, remove cart from local storage
    if (isNotFound && isCart) {
      localStorage.removeItem(domain)
      window.location.reload()
    }

    // On 401 not found error and operation is PartnerAndCustomer, remove customer related credentials from local storage
    if (isNotFound && isPartnerAndCustomer) {
      removeLoginCredentials(domain)
      window.location.reload()
    }

    if (customerIdDoesNotMatch && isPartner) {
      removeLoginCredentials(domain)
      window.location.reload()
    }

    // On cart already ordered error, remove cart from local storage
    if (isCartAlreadyOrdered) {
      localStorage.removeItem(domain)
      window.location.reload()
    }

    if (isUnauthorized) {
      removeLoginCredentials(domain)
      window.location.reload()
    }

    // Remove customer id/api key/csrf token from local storage if unauthorized
    if (errorMessage) {
      const isOrder = operation.operationName === 'order'
      // Bypass global handler for /track page, handled locally
      if (errorMessage === 'unauthorized') {
        if (isOrder) return
        removeLoginCredentials(domain)
        window.location.reload()
      }
      if (errorMessage === 'auth_missing') {
        removeLoginCredentials(domain)
        if (!isOrder) window.location.reload()
      }
    }
  })

  /** Sets CSRF token in request headers & local storage for consumer API */
  const consumerResponseLink = new ApolloLink((operation, forward) => {
    if (forward) {
      // Get the csrf token from local storage if it exists
      const csrfToken = localStorage.getItem(`${domain}-csrf`)
      // Dynamically adds the csrf token to request headers
      csrfToken &&
        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            'x-csrf-token': csrfToken
          }
        }))

      // Gets csrf token from response headers and sets it in local storage
      return forward(operation).map((response) => {
        const context = operation.getContext()
        const responseHeaders = context.response.headers
        const csrfToken = responseHeaders.get('x-csrf-token')
        if (csrfToken) {
          localStorage.setItem(`${domain}-csrf`, csrfToken)
        }
        return response
      })
    } else {
      return Observable.of()
    }
  })

  /** Combines response/error link functions & consumer http/ws link */
  const consumerLinkWithMiddleware = ApolloLink.from([
    getRequestHeadersMiddleware(partnerId, domain),
    errorLink,
    consumerResponseLink,
    consumerApiLink
  ])

  return split(
    (operation) => operation.getContext().clientName === 'consumerApi',
    consumerLinkWithMiddleware,
    ApolloLink.from([
      getRequestHeadersMiddleware(partnerId, domain),
      apiAndWebsocketLink
    ])
  )
}

/** Create an Absinthe Socket Link and split it with the other global links. */
const getWebSocketLink = (
  trackingToken: string,
  domain: string,
  apiKey: string,
  partnerId: string,
  errorCallback?: () => any,
  successCallback?: () => any
) => {
  const { websocketLink, socket } = createCustomAbsintheLink(
    WSS_CONSUMER_ENDPOINT,
    {
      'x-order-tracking-token': trackingToken
    },
    errorCallback,
    successCallback
  )
  return {
    link: getClientLinks(domain, apiKey, partnerId, websocketLink),
    socket
  }
}

const getRequestHeadersMiddleware = (partnerId: string, domain: string) =>
  new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        'x-hasura-merchant-id': partnerId || domain, // fallback to "merchant.slug"
        'x-hasura-operation-name': operation.operationName
      }
    }))

    return forward(operation)
  })

/**
 *  Shop props
 */
interface ShopProps {
  config: ShopConfig
  children: React.ReactNode
}

/**
 *  A hook to return a wrapped graphql client
 *
 *  @returns ApolloClient
 */
const useShopClient = () => {
  return useApolloClient()
}

/**
 * The Slerp context provider that allows the app to connect
 * to the GraphQL endpoint
 *
 * @param props An object that accepts a config (ShopConfig) and its children.
 *
 * @example
 *
 *  const config = {
 *   apiKey: 'someapikey',
 *   domain: 'crosstown'
 *  }
 *
 *  <ShopClient config={config}>
 *    <SomeComponent />
 *  </ShopClient>
 *
 */
const ShopClient = (props: ShopProps) => {
  const {
    config: { domain, apiKey, consumerApiHost }
  } = props

  // Set consumer API URI dynamically on load to get consumer api host from root element /config
  if (consumerApiHost) {
    consumerApiUri = `${scheme('http')}://${consumerApiHost}/api/consumer/v2`
  } else {
    // Temporarily fallback: use old uri if partner does not have attribute set
    consumerApiUri = `${scheme('http')}://${domain}.${
      process.env.REACT_APP_CONSUMER_GRAPH_HOST
    }/api/consumer/v2`
  }

  return (
    <ApolloProvider client={createClient(domain, apiKey, '')}>
      {props.children}
    </ApolloProvider>
  )
}

export * from './queries'
export * from './subscriptions'
export * from './mutations/CheckoutMutations'
export { useShopClient, createClient, getWebSocketLink, getClientLinks }
export default ShopClient
